import {
	Box,
	Stack,
	Snackbar,
	Alert,
	Typography,
	TextField,
	Button,
	InputAdornment,
	IconButton,
	Divider,
	Paper,
	Avatar,
	Tooltip,
	Switch,
} from "@mui/material";
import {
	Visibility,
	VisibilityOff,
	EmojiEvents as EmojiEventsIcon,
	ConfirmationNumber as ConfirmationNumberIcon,
	Edit as EditIcon,
	CameraAlt as CameraAltIcon,
} from "@mui/icons-material";
import { useState, useRef, useEffect } from "react";
import { generateButtonSx } from "../../../utils/elementor-style-override";
import { StyledDatePicker, datePickerPopperSx } from "../../../components/ui/CalendarPopper";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import useUpdateAgentProfile from "../hooks/useUpdateAgentProfile";
import useUpdateAgentEmail from "../hooks/useUpdateAgentEmail";
import useUpdateAgentPassword from "../hooks/useUpdateAgentPassword";
import useUpdateNotificationPreferences from "../hooks/useUpdateNotifPreference";
import ConfirmationModal from "../../../components/confirmation-modal";
import AgentPreferenceChecklist from "./AgentPreferences/AgentPreferenceChecklist";

const primaryBlue = "#1D54A2";
const primaryBlueDark = "#1A3F7A";
const primaryBlueLight = "#EBF5FF";

const sharedContainedSx = {
	backgroundColor: `${primaryBlue} !important`,
	...generateButtonSx({ bg: `${primaryBlue} !important`, text: "#fff" }, { bg: `${primaryBlue} !important`, text: "#fff !important" }),
};

const sharedOutlinedSx = {
	borderColor: `${primaryBlue} !important`,
	color: `${primaryBlue} !important`,
	backgroundColor: "#FFFFFF !important",
	...generateButtonSx({ bg: "#EBF5FF !important", text: "#1D54A2" }, { bg: "#EBF5FF !important", text: "#1D54A2" }),
};

// Patches the cached logged-in user record so the rest of the app
// (header avatar, greetings, etc.) stays in sync after a save.
const patchLoggedInData = (patch) => {
	try {
		const stored = localStorage.getItem("loggedin_data");
		const current = stored ? JSON.parse(stored) : {};
		const updated = { ...current, ...patch };
		localStorage.setItem("loggedin_data", JSON.stringify(updated));
	} catch {
		// ignore malformed cache, nothing to patch
	}
};

export default function UpdateProfile({ profileData, section = "profile" }) {
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMsg, setSnackbarMsg] = useState("");
	const [snackbarSeverity, setSnackbarSeverity] = useState("info");

	const { updateAgentProfile, loading: updateProfileLoading } = useUpdateAgentProfile();
	const { updateAgentEmail, loading: updateEmailLoading } = useUpdateAgentEmail();
	const { updateAgentPassword, loading: updatePasswordLoading } = useUpdateAgentPassword();
	const { updateNotificationPreferences, loading: updatePrefsLoading } = useUpdateNotificationPreferences();

	// Committed avatar (what's actually saved on the server)
	const [avatarUrl, setAvatarUrl] = useState(profileData?.avatar || "");
	// Locally chosen file + its preview, staged until Save is clicked
	const [pendingAvatarFile, setPendingAvatarFile] = useState(null);
	const [pendingAvatarPreview, setPendingAvatarPreview] = useState("");
	const fileInputRef = useRef(null);

	// Personal details — source of truth once saved
	const [personalFields, setPersonalFields] = useState({
		name: profileData?.name || "",
		last_name: profileData?.last_name || "",
		address: profileData?.address || "",
		phone: profileData?.phone || "",
		dob: profileData?.dob || "",
	});

	// Read-only fields, never editable
	const readOnlyFields = {
		npn_number: profileData?.npn_number || "",
		license_number: profileData?.license_number || "",
		agency_name: profileData?.agency_name || "",
	};

	// Whole-section edit mode
	const [isEditing, setIsEditing] = useState(false);
	const [draftFields, setDraftFields] = useState(personalFields);

	// Email state
	const [newEmail, setNewEmail] = useState("");
	const [confirmEmail, setConfirmEmail] = useState("");

	// Password state
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showCurrentPw, setShowCurrentPw] = useState(false);
	const [showNewPw, setShowNewPw] = useState(false);
	const [showConfirmPw, setShowConfirmPw] = useState(false);

	// Notification preferences state — seeded from profileData if the backend
	// already returned saved prefs, otherwise default to off.
	const [milestoneAlerts, setMilestoneAlerts] = useState(profileData?.notification_preferences?.milestone_alerts ?? true);
	const [ticketAlerts, setTicketAlerts] = useState(profileData?.notification_preferences?.ticket_alerts ?? true);

	// Pending toggle awaiting user confirmation via modal — holds everything
	// needed to apply (or discard) the change once the user decides.
	const [pendingToggle, setPendingToggle] = useState(null);

	// Revoke the blob preview URL when it's replaced or on unmount, to avoid leaks
	useEffect(() => {
		return () => {
			if (pendingAvatarPreview) URL.revokeObjectURL(pendingAvatarPreview);
		};
	}, [pendingAvatarPreview]);

	const showSnackbar = (msg, severity = "info") => {
		setSnackbarMsg(msg);
		setSnackbarSeverity(severity);
		setSnackbarOpen(true);
	};

	const handleSnackbarClose = () => setSnackbarOpen(false);

	const personalFieldConfig = [
		{ key: "name", label: "First Name" },
		{ key: "last_name", label: "Last Name" },
		{ key: "address", label: "Address" },
		{ key: "phone", label: "Phone" },
	];

	const startEditing = () => {
		setDraftFields(personalFields);
		setIsEditing(true);
	};

	const cancelEditing = () => {
		setDraftFields(personalFields);
		if (pendingAvatarPreview) URL.revokeObjectURL(pendingAvatarPreview);
		setPendingAvatarFile(null);
		setPendingAvatarPreview("");
		setIsEditing(false);
	};

	const updateDraft = (key, value) => {
		setDraftFields((f) => ({ ...f, [key]: value }));
	};

	const saveFields = async () => {
		const formData = new FormData();

		formData.append("agent_id", profileData?.id ?? "");
		formData.append(
			"data",
			JSON.stringify({
				first_name: draftFields.name,
				last_name: draftFields.last_name,
				address: draftFields.address,
				phone: draftFields.phone,
				dob: draftFields.dob,
			})
		);

		if (pendingAvatarFile) {
			formData.append("attachment", pendingAvatarFile);
		}

		const { success, data, error } = await updateAgentProfile(formData);

		if (!success) {
			showSnackbar(error || "Failed to update profile.", "error");
			return;
		}

		const updated = data?.data || {};

		setPersonalFields(draftFields);

		const patch = { ...draftFields };
		if (pendingAvatarFile) {
			const finalAvatarUrl = updated.avatar_url || pendingAvatarPreview;
			setAvatarUrl(finalAvatarUrl);
			patch.avatar = finalAvatarUrl;
			if (pendingAvatarPreview) URL.revokeObjectURL(pendingAvatarPreview);
			setPendingAvatarFile(null);
			setPendingAvatarPreview("");
		}

		patchLoggedInData(patch);
		showSnackbar(data?.message || "Profile updated successfully.", "success");
		setIsEditing(false);
	};

	const handleAvatarClick = () => {
		if (!isEditing) return;
		fileInputRef.current?.click();
	};

	const handleAvatarFileChange = (e) => {
		const file = e.target.files?.[0];
		if (!file) return;

		if (!file.type.startsWith("image/")) {
			showSnackbar("Please select a valid image file.", "warning");
			if (fileInputRef.current) fileInputRef.current.value = "";
			return;
		}
		if (file.size > 5 * 1024 * 1024) {
			showSnackbar("Image must be smaller than 5MB.", "warning");
			if (fileInputRef.current) fileInputRef.current.value = "";
			return;
		}

		if (pendingAvatarPreview) URL.revokeObjectURL(pendingAvatarPreview);
		const previewUrl = URL.createObjectURL(file);
		setPendingAvatarFile(file);
		setPendingAvatarPreview(previewUrl);
	};

	const handleEmailUpdate = async () => {
		if (!newEmail || !confirmEmail) return showSnackbar("Please fill in both email fields.", "warning");
		if (newEmail !== confirmEmail) return showSnackbar("Emails do not match.", "error");
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(newEmail)) return showSnackbar("Please enter a valid email address.", "error");

		const { success, data, error } = await updateAgentEmail(newEmail);

		if (!success) {
			showSnackbar(error, "error");
			return;
		}

		patchLoggedInData({ email: newEmail });
		showSnackbar(data?.message || "Email updated successfully.", "success");
		setNewEmail("");
		setConfirmEmail("");
	};

	const handlePasswordUpdate = async () => {
		if (!currentPassword || !newPassword || !confirmPassword) return showSnackbar("Please fill in all password fields.", "warning");
		if (newPassword !== confirmPassword) return showSnackbar("New passwords do not match.", "error");
		if (newPassword.length < 8) return showSnackbar("Password must be at least 8 characters.", "warning");

		const { success, data, error } = await updateAgentPassword(currentPassword, newPassword);

		if (!success) {
			showSnackbar(error, "error");
			return;
		}

		showSnackbar(data?.message || "Password updated successfully.", "success");
		setCurrentPassword("");
		setNewPassword("");
		setConfirmPassword("");
	};

	// Opens the confirmation modal instead of flipping the switch right away.
	const requestToggleAlert = (prefKey, currentValue, setter, label) => {
		setPendingToggle({ prefKey, currentValue, setter, label, newValue: !currentValue });
	};

	const closeToggleModal = () => {
		if (updatePrefsLoading) return; // don't allow closing mid-request
		setPendingToggle(null);
	};

	// Confirmed from the modal — apply optimistically, persist, revert on failure.
	const confirmToggleAlert = async () => {
		if (!pendingToggle) return;
		const { prefKey, currentValue, setter, newValue } = pendingToggle;

		setter(newValue);

		const { success, error } = await updateNotificationPreferences({ [prefKey]: newValue });

		if (!success) {
			setter(currentValue);
			showSnackbar(error || "Failed to update notification preference.", "error");
			setPendingToggle(null);
			return;
		}

		showSnackbar("Notification preference updated.", "success");
		setPendingToggle(null);
	};

	const notificationItems = [
		{
			key: "milestone_alerts",
			label: "Milestone alerts",
			desc: "Be informed when key milestones are reached",
			icon: <EmojiEventsIcon fontSize="small" />,
			iconBg: "#E8F5E9",
			iconColor: "#2E7D32",
			value: milestoneAlerts,
			setter: setMilestoneAlerts,
		},
		{
			key: "ticket_alerts",
			label: "Support ticket updates",
			desc: "Get notified whenever there's an update on your support tickets",
			icon: <ConfirmationNumberIcon fontSize="small" />,
			iconBg: "#EBF5FF",
			iconColor: primaryBlue,
			value: ticketAlerts,
			setter: setTicketAlerts,
		},
	];

	// What the Avatar actually shows: staged preview while editing, otherwise the saved one
	const displayedAvatar = pendingAvatarPreview || avatarUrl;

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			{section === "profile" && (
				<>
			{/* Personal Details */}
			<Box sx={{ position: "relative", pr: { xs: 0, md: "10px" }, width: "100% !important" }}>
				<Typography variant="h5" fontWeight={600} mb={3}>
					Personal Details
				</Typography>

				<Box
					sx={{
						position: { xs: "static", md: "absolute" },
						top: { md: 0 },
						right: { md: 50 },
						mb: { xs: 3, md: 0 },
						display: "flex",
						justifyContent: { xs: "center", md: "flex-end" },
					}}
				>
					<Box sx={{ position: "relative", width: 130, height: 130 }}>
						<Avatar
							src={displayedAvatar || undefined}
							sx={{
								width: 130,
								height: 130,
								bgcolor: primaryBlueLight,
								border: `2px solid ${primaryBlueDark}`,
								fontSize: "2rem",
								color: "#3A7DC9",
							}}
						>
							{!displayedAvatar && "?"}
						</Avatar>

						<input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleAvatarFileChange} />

						{isEditing && (
							<Tooltip title="Change photo" arrow>
								<IconButton
									onClick={handleAvatarClick}
									size="small"
									sx={{
										position: "absolute",
										bottom: 0,
										right: 0,
										width: 34,
										height: 34,
										backgroundColor: `${primaryBlue} !important`,
										border: "2px solid #fff",
										boxShadow: 2,
										"&:hover": { backgroundColor: `${primaryBlueDark} !important` },
									}}
								>
									<CameraAltIcon sx={{ fontSize: 16, color: "#fff" }} />
								</IconButton>
							</Tooltip>
						)}
					</Box>
				</Box>

				<Stack direction={{ xs: "column", md: "row" }} alignItems={{ xs: "stretch", md: "flex-start" }} gap={3} pt={{ xs: 0, md: 3 }}>
					{/* Editable fields */}
					<Box flex={1} sx={{ width: "100% !important" }}>
						{personalFieldConfig.map(({ key, label }) => (
							<Box mb={2} key={key}>
								<Typography variant="subtitle1" fontWeight={600} mb={1}>
									{label}
								</Typography>
								{isEditing ? (
									<TextField
										value={draftFields[key]}
										onChange={(e) => updateDraft(key, e.target.value)}
										size="small"
										fullWidth
										disabled={updateProfileLoading}
									/>
								) : (
									<Typography variant="body1" sx={{ color: "#333" }}>
										{personalFields[key] || "—"}
									</Typography>
								)}
							</Box>
						))}

						<Box mb={2}>
							<Typography variant="subtitle1" fontWeight={600} mb={1}>
								Date of Birth
							</Typography>
							{isEditing ? (
								<StyledDatePicker
									value={draftFields.dob ? dayjs(draftFields.dob) : null}
									onChange={(newVal) => updateDraft("dob", newVal ? newVal.format("YYYY-MM-DD") : "")}
									disabled={updateProfileLoading}
									slotProps={{
										textField: { size: "small", fullWidth: true },
										popper: { sx: datePickerPopperSx },
									}}
								/>
							) : (
								<Typography variant="body1" sx={{ color: "#333" }}>
									{personalFields.dob || "—"}
								</Typography>
							)}
						</Box>
					</Box>

					{/* Read-only fields */}
					<Box flex={1} alignSelf={{ xs: "stretch", md: "flex-start" }}>
						{[
							{ label: "NPN", value: readOnlyFields.npn_number },
							{ label: "License Number", value: readOnlyFields.license_number },
							{ label: "Agency Name", value: readOnlyFields.agency_name },
						].map(({ label, value }) => (
							<Box mb={2} key={label}>
								<Typography variant="subtitle1" fontWeight={600} mb={1}>
									{label}
								</Typography>
								<Typography variant="body1" sx={{ color: "#333" }}>
									{value || "—"}
								</Typography>
							</Box>
						))}
					</Box>
				</Stack>

				<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 1 }}>
					{isEditing ? (
						<>
							<Button variant="outlined" onClick={cancelEditing} disabled={updateProfileLoading} sx={sharedOutlinedSx}>
								Cancel
							</Button>
							<Button variant="contained" onClick={saveFields} disabled={updateProfileLoading} sx={sharedContainedSx}>
								{updateProfileLoading ? "Saving..." : "Save"}
							</Button>
						</>
					) : (
						<Button variant="outlined" startIcon={<EditIcon sx={{ fontSize: 16 }} />} onClick={startEditing} sx={sharedOutlinedSx}>
							Edit
						</Button>
					)}
				</Box>
			</Box>

			<Box sx={{ my: 4 }}>
				<Divider />
			</Box>

			{/* Update Credentials */}
			<Typography variant="h5" fontWeight={600} mb={3}>
				Update Credentials
			</Typography>
			<Stack direction={{ xs: "column", md: "row" }} spacing={3}>
				<Paper variant="outlined" sx={{ flex: 1, p: 3, borderRadius: 2, width: "100% !important" }}>
					<Typography variant="subtitle1" fontWeight={600} mb={0.5}>
						Email Address
					</Typography>
					<Typography variant="body2" color="text.secondary" mb={2.5}>
						Update your account email address.
					</Typography>
					<Stack spacing={2}>
						<TextField
							placeholder="New Email"
							type="email"
							value={newEmail}
							onChange={(e) => setNewEmail(e.target.value)}
							fullWidth
							size="small"
							autoComplete="off"
						/>
						<TextField
							placeholder="Confirm New Email"
							type="email"
							value={confirmEmail}
							onChange={(e) => setConfirmEmail(e.target.value)}
							fullWidth
							size="small"
							autoComplete="off"
						/>
						<Box>
							<Button variant="contained" onClick={handleEmailUpdate} disabled={updateEmailLoading} sx={sharedContainedSx}>
								{updateEmailLoading ? "Updating..." : "Update Email"}
							</Button>
						</Box>
					</Stack>
				</Paper>

				<Paper variant="outlined" sx={{ flex: 1, p: 3, borderRadius: 2, width: "100% !important" }}>
					<Typography variant="subtitle1" fontWeight={600} mb={0.5}>
						Password
					</Typography>
					<Typography variant="body2" color="text.secondary" mb={2.5}>
						Change your account password.
					</Typography>
					<Stack spacing={2}>
						<TextField
							placeholder="Current Password"
							type={showCurrentPw ? "text" : "password"}
							value={currentPassword}
							onChange={(e) => setCurrentPassword(e.target.value)}
							fullWidth
							size="small"
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											size="small"
											onClick={() => setShowCurrentPw((v) => !v)}
											edge="end"
											sx={{ backgroundColor: "transparent !important" }}
										>
											{showCurrentPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
										</IconButton>
									</InputAdornment>
								),
							}}
						/>
						<TextField
							placeholder="New Password"
							type={showNewPw ? "text" : "password"}
							value={newPassword}
							onChange={(e) => setNewPassword(e.target.value)}
							fullWidth
							size="small"
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											size="small"
											onClick={() => setShowNewPw((v) => !v)}
											edge="end"
											sx={{ backgroundColor: "transparent !important" }}
										>
											{showNewPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
										</IconButton>
									</InputAdornment>
								),
							}}
						/>
						<TextField
							placeholder="Confirm New Password"
							type={showConfirmPw ? "text" : "password"}
							value={confirmPassword}
							onChange={(e) => setConfirmPassword(e.target.value)}
							fullWidth
							size="small"
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											size="small"
											onClick={() => setShowConfirmPw((v) => !v)}
											edge="end"
											sx={{ backgroundColor: "transparent !important" }}
										>
											{showConfirmPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
										</IconButton>
									</InputAdornment>
								),
							}}
						/>
						<Box>
							<Button variant="contained" onClick={handlePasswordUpdate} disabled={updatePasswordLoading} sx={sharedContainedSx}>
								{updatePasswordLoading ? "Updating..." : "Update Password"}
							</Button>
						</Box>
					</Stack>
				</Paper>
			</Stack>
				</>
			)}

			{section === "preferences" && (
				<>
			{/* Notification Preferences */}
			<Typography variant="h5" fontWeight={600} mb={3}>
				Notification Preferences
			</Typography>
			<Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
				{notificationItems.map(({ key, label, desc, icon, iconBg, iconColor, value, setter }) => (
					<Paper
						key={key}
						variant="outlined"
						sx={{
							flex: 1,
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							p: "12px 16px",
							borderRadius: 2,
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
							<Box
								sx={{
									width: 36,
									height: 36,
									borderRadius: 1.5,
									bgcolor: iconBg,
									color: iconColor,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									flexShrink: 0,
								}}
							>
								{icon}
							</Box>
							<Box>
								<Typography variant="body2" fontWeight={600}>
									{label}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{desc}
								</Typography>
							</Box>
						</Box>
						<Switch checked={value} disabled={updatePrefsLoading} onChange={() => requestToggleAlert(key, value, setter, label)} />
					</Paper>
				))}
			</Stack>

			<AgentPreferenceChecklist showSnackbar={showSnackbar} />
				</>
			)}

			<ConfirmationModal
				open={!!pendingToggle}
				onClose={closeToggleModal}
				onConfirm={confirmToggleAlert}
				isLoading={updatePrefsLoading}
				title={pendingToggle?.newValue ? "Turn on this notification?" : "Turn off this notification?"}
				description={
					pendingToggle
						? pendingToggle.newValue
							? `You'll start receiving "${pendingToggle.label}" notifications.`
							: `You'll stop receiving "${pendingToggle.label}" notifications.`
						: ""
				}
				confirmText={pendingToggle?.newValue ? "Yes, Turn On" : "Yes, Turn Off"}
				cancelText="No, Go Back"
			/>

			<Snackbar
				open={snackbarOpen}
				autoHideDuration={6000}
				onClose={handleSnackbarClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={handleSnackbarClose}
					severity={snackbarSeverity}
					sx={{
						width: "100%",
						"& .MuiAlert-action button": { backgroundColor: "transparent" },
					}}
				>
					{snackbarMsg}
				</Alert>
			</Snackbar>
		</LocalizationProvider>
	);
}
